import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, InputGroup, Nav, Spinner, Table } from 'react-bootstrap';
import { ChevronDown, ChevronRight, Search } from 'react-bootstrap-icons';
import { DeployBlockDialog } from '@/components/clientFlags/ClientTagDialog';
import { DoNotDeployTag } from '@/components/clientFlags/ClientTags';
import { ConfirmDialog } from '@/components/deployments/ConfirmDialog';
import { SegmentedBar } from '@/components/deployments/DeployProgressBar';
import { FailureLine } from '@/components/deployments/FailureLine';
import { RefreshButton } from '@/components/deployments/RefreshButton';
import { RunActionDialog, type RunAction } from '@/components/deployments/RunActionDialog';
import { RunDetails } from '@/components/deployments/RunDetails';
import { RunMoreMenu } from '@/components/deployments/RunMoreMenu';
import { RowProgress } from '@/components/deployments/RowProgress';
import { SkippedClientsNotice } from '@/components/deployments/SkippedClientsNotice';
import { StatusPill } from '@/components/deployments/StatusPill';
import { CopyButton } from '@/components/ui/CopyButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useClientFlags } from '@/hooks/useClientFlags';
import { useDeployClock } from '@/hooks/useDeployClock';
import { useDeployFailures } from '@/hooks/useDeployFailures';
import { useDeployImages } from '@/hooks/useDeployImages';
import { PROGRESS_PRIORITY, useDeployProgress } from '@/hooks/useDeployProgress';
import { clientService } from '@/services/clientService';
import {
  BlockedClientsChangedError,
  getGroupDeploymentSummary,
  getLatestDeploymentForClient,
  getSkippedClients,
  listDeploymentsForGroup,
  startGroupDeployment,
  type DeploymentRecord,
} from '@/services/deploymentService';
import { shortPerson } from '@/utils/clientFlags';
import { describeSkips, skipLine, splitGroupClients, type GroupSkippedClient } from '@/utils/groupRunSkips';
import { retryUnavailableFailures } from '@/utils/deployFailureStore';
import {
  groupCounts,
  groupDisplayName,
  imageDisplay,
  isFailed,
  isLive,
  isStuck,
  newestRunByClient,
  shortUser,
  statusKey,
} from '@/utils/deployStatus';
import { formatDuration, formatNzDate, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import '@/components/deployments/deployments.css';

type MemberFilter = 'all' | 'running' | 'failed' | 'succeeded' | 'waiting';

const LIVE_REFRESH_MS = 10_000;
const COLUMNS = 5;

/** A client of the group that has no run of its own: waiting for its turn, or never started. */
const isPlaceholder = (record: DeploymentRecord) => {
  const status = statusKey(record);
  return status === 'waiting' || status === 'not-started';
};

/** A member's duration. A live one counts up each second. */
function MemberDuration({ record }: { record: DeploymentRecord }) {
  const live = isLive(record);
  const now = useDeployClock(live);
  if (!record.startedAt) return null;
  return (
    <>
      <span className="numeric">{formatDuration(record.startedAt, record.endedAt, now)}</span>
      {live && isStuck(record, now) && <div className="deploy-sub deploy-sub--danger">Stuck?</div>}
    </>
  );
}

function GroupDuration({ summary }: { summary: DeploymentRecord }) {
  const live = isLive(summary);
  const now = useDeployClock(live);
  return <span className="numeric">{formatDuration(summary.startedAt, summary.endedAt, now) || 'Unknown'}</span>;
}

export default function GroupDeploymentDetail() {
  const { groupRunId = '' } = useParams<{ groupRunId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [summary, setSummary] = useState<DeploymentRecord | null>(null);
  const [members, setMembers] = useState<DeploymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);
  const [filter, setFilter] = useState<MemberFilter>('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [action, setAction] = useState<RunAction | null>(null);
  const [actionNewest, setActionNewest] = useState<DeploymentRecord | undefined>();
  const [confirmRunFailed, setConfirmRunFailed] = useState(false);
  const [runningFailed, setRunningFailed] = useState(false);
  const [runFailedError, setRunFailedError] = useState<string | null>(null);
  // The clients this run left out when it started, and why.
  const [skipped, setSkipped] = useState<GroupSkippedClient[]>([]);
  // Every client name with a config, for the second pass. Null while it loads, or when it did not load.
  const [knownClients, setKnownClients] = useState<ReadonlySet<string> | null>(null);
  const [knownClientsFailed, setKnownClientsFailed] = useState(false);
  // The client whose do-not-deploy block a row's menu asked to set or lift.
  const [blockingClient, setBlockingClient] = useState<string | null>(null);
  const flags = useClientFlags();

  const shownRun = useRef(groupRunId);
  shownRun.current = groupRunId;

  const loadData = useCallback(
    async (quiet = false) => {
      if (!groupRunId) return;
      if (!quiet) setRefreshing(true);
      try {
        const [summaryRecord, memberRecords] = await Promise.all([
          getGroupDeploymentSummary(groupRunId, !quiet),
          listDeploymentsForGroup(groupRunId),
        ]);
        setSummary(summaryRecord);
        setMembers(memberRecords);
        setRefreshedAt(Date.now());
        if (!quiet) setError(null);
        // The skipped list is written once, at the start. It is read when the page opens and on
        // Refresh, never on the timer. A failed read leaves the section out and breaks nothing.
        if (!quiet) {
          getSkippedClients(groupRunId)
            .then((record) => {
              // The page may show another run by the time this is back.
              if (shownRun.current === groupRunId) setSkipped(record?.skipped ?? []);
            })
            .catch((e) => console.warn('Could not read the clients this run skipped', e));
        }
      } catch (e) {
        console.error('Failed to load group deployment details', e);
        // A missed background refresh is not worth an alert: the next one is ten seconds away.
        if (!quiet) setError(e instanceof Error ? e.message : 'The read failed.');
      } finally {
        if (!quiet) setRefreshing(false);
        setLoading(false);
      }
    },
    [groupRunId]
  );

  useEffect(() => {
    setLoading(true);
    // Another run's skipped clients must never show under this one.
    setSkipped([]);
    void loadData();
  }, [loadData]);

  // A second pass skips a name that is no longer a client, so it needs the client list. One read.
  useEffect(() => {
    let cancelled = false;
    clientService
      .getAllClients()
      .then((all) => {
        if (!cancelled) setKnownClients(new Set(all.map((c) => c.name)));
      })
      .catch((e) => {
        console.warn('Could not read the client list', e);
        if (!cancelled) setKnownClientsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Each client's newest run in this group. An older failed row for the same client is history.
  const newestByClient = useMemo(() => newestRunByClient(members), [members]);
  const liveClients = useMemo(() => new Set(members.filter(isLive).map((m) => m.clientName)), [members]);
  const groupIsLive = summary ? isLive(summary) : false;
  const anyLive = groupIsLive || liveClients.size > 0;

  // While anything is running the page reads the group every ten seconds, and stops when the tab is hidden.
  useEffect(() => {
    if (!anyLive) return;
    const tick = () => {
      if (document.visibilityState === 'visible') void loadData(true);
    };
    const timer = setInterval(tick, LIVE_REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [anyLive, loadData]);

  /** Every client of the group as one list: its runs, plus a line for a client that has no run yet. */
  const rows = useMemo(() => {
    const started = new Set(members.map((m) => m.clientName));
    const placeholders: DeploymentRecord[] = (summary?.clients || [])
      .filter((client) => !started.has(client))
      .map((client) => ({
        deploymentId: `${groupRunId}#${client}#none`,
        clientName: client,
        status: groupIsLive ? 'waiting' : 'not-started',
      }));
    return [...members, ...placeholders].sort(
      (a, b) => a.clientName.localeCompare(b.clientName) || (b.startedAt || '').localeCompare(a.startedAt || '')
    );
  }, [members, summary, groupRunId, groupIsLive]);

  const counts = useMemo(() => (summary ? groupCounts(summary, members) : null), [summary, members]);
  // The image by its given name, once the names arrive. Its tag until then.
  const imageNames = useDeployImages(summary ? (summary.repository ?? 'numa-deploy') : undefined);

  // The clients whose newest run in this group failed. A client that passed on a second run here is not one of them.
  const failedHere = useMemo(() => {
    const names: string[] = [];
    for (const [client, record] of newestByClient) if (isFailed(record)) names.push(client);
    return names.sort();
  }, [newestByClient]);

  // A failed client may have been deployed again since, outside this group run: by hand, or in a
  // later group run. This page only holds its own rows, so it asks for each failed client's newest
  // run anywhere, once. Without that, an old group run would offer to put its image back on
  // clients that moved on days ago.
  const [latestByClient, setLatestByClient] = useState<Record<string, DeploymentRecord | null>>({});
  const askedFor = useRef(new Set<string>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    const todo = failedHere.filter((client) => !askedFor.current.has(client));
    if (todo.length === 0) return;
    for (const client of todo) askedFor.current.add(client);
    (async () => {
      // Eight small reads at a time, each a few rows from the per-client index.
      for (let i = 0; i < todo.length && mounted.current; i += 8) {
        const chunk = todo.slice(i, i + 8);
        const found = await Promise.all(chunk.map((client) => getLatestDeploymentForClient(client).catch(() => null)));
        if (!mounted.current) return;
        setLatestByClient((prev) => {
          const next = { ...prev };
          chunk.forEach((client, index) => (next[client] = found[index]));
          return next;
        });
      }
    })();
  }, [failedHere]);

  /** A run of the same client that started after this one: in this group, or anywhere else. */
  const newerRunFor = useCallback(
    (member: DeploymentRecord): DeploymentRecord | undefined => {
      const inGroup = newestByClient.get(member.clientName);
      if (inGroup && inGroup.deploymentId !== member.deploymentId) return inGroup;
      const latest = latestByClient[member.clientName];
      if (
        latest &&
        latest.deploymentId !== member.deploymentId &&
        (latest.startedAt || '') > (member.startedAt || '')
      ) {
        return latest;
      }
      return undefined;
    },
    [newestByClient, latestByClient]
  );
  const checkingFailed = failedHere.some((client) => !(client in latestByClient));

  // The clients to offer a second run: failed here, and not deployed again since.
  const failedClients = useMemo(
    () =>
      failedHere.filter((client) => {
        const record = newestByClient.get(client);
        return client in latestByClient && record !== undefined && !newerRunFor(record);
      }),
    [failedHere, newestByClient, latestByClient, newerRunFor]
  );
  const movedOn = failedHere.length - failedClients.length;

  const filterCounts = useMemo(() => {
    const by: Record<MemberFilter, number> = { all: rows.length, running: 0, failed: 0, succeeded: 0, waiting: 0 };
    for (const row of rows) {
      if (isLive(row)) by.running += 1;
      else if (isFailed(row)) by.failed += 1;
      else if (statusKey(row) === 'success') by.succeeded += 1;
      else if (isPlaceholder(row)) by.waiting += 1;
    }
    return by;
  }, [rows]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (term && !row.clientName.toLowerCase().includes(term)) return false;
      if (filter === 'running') return isLive(row);
      if (filter === 'failed') return isFailed(row);
      if (filter === 'succeeded') return statusKey(row) === 'success';
      if (filter === 'waiting') return isPlaceholder(row);
      return true;
    });
  }, [rows, filter, search]);

  // One read covers the reason for up to 100 failed members.
  const rowsNeedingReason = useMemo(
    () => visibleRows.filter((row) => isFailed(row) || (statusKey(row) === 'resolved' && expanded[row.deploymentId])),
    [visibleRows, expanded]
  );
  const failures = useDeployFailures(rowsNeedingReason, expanded);

  // Live progress for the running members on screen. The shared poller reads six at a time.
  const runningVisible = useMemo(() => visibleRows.filter(isLive), [visibleRows]);
  const progress = useDeployProgress(runningVisible, PROGRESS_PRIORITY.member);

  // "Run again" can put an older image back on a client. Find the client's newest run anywhere, to warn about it.
  const actionSeq = useRef(0);
  const openAction = (next: RunAction) => {
    setAction(next);
    setActionNewest(next.type === 'run-again' ? newerRunFor(next.record) : undefined);
    if (next.type !== 'run-again') return;
    const seq = ++actionSeq.current;
    getLatestDeploymentForClient(next.record.clientName)
      .then((newest) => {
        if (seq === actionSeq.current && newest && newest.deploymentId !== next.record.deploymentId) {
          if ((newest.startedAt || '') > (next.record.startedAt || '')) setActionNewest(newest);
        }
      })
      .catch((e) => console.warn('Could not check for a newer run', e));
  };

  const handlers = {
    onRunAgain: (record: DeploymentRecord) => openAction({ type: 'run-again', record }),
    onStop: (record: DeploymentRecord) => openAction({ type: 'stop', record }),
    onMarkFailed: (record: DeploymentRecord) => openAction({ type: 'mark-failed', record }),
    onMarkResolved: (record: DeploymentRecord) => openAction({ type: 'mark-resolved', record }),
    onBlockDeploys: (record: DeploymentRecord) => setBlockingClient(record.clientName),
  };

  // The second pass is a group run, so it leaves out a failed client that is marked do not deploy
  // or inactive, or is no longer a client.
  const failedSplit = useMemo(
    () => splitGroupClients(failedClients, flags, knownClients),
    [failedClients, flags, knownClients]
  );

  const runFailedAgain = async () => {
    if (!user?.email || !summary || failedClients.length === 0) return;
    setRunningFailed(true);
    setRunFailedError(null);
    try {
      const { groupRunId: newRunId } = await startGroupDeployment({
        groupName: summary.groupName || `Retry ${groupRunId}`,
        // Every failed client goes in. The start takes out the ones marked do not deploy, and
        // refuses when they are not the ones the confirm step listed.
        clients: failedClients,
        shownSkipped: failedSplit.skipped.map((s) => s.clientName),
        imageTag: summary.imageTag || 'latest',
        // The deploy pipeline needs the channel. Rows from before channels existed are production.
        repository: summary.repository ?? 'numa-deploy',
        initiatedBy: user.email,
        maxConcurrency: summary.maxConcurrency,
      });
      setConfirmRunFailed(false);
      // The second pass is a group run of its own. Go to it, so the person watches the run they started.
      navigate(`/deployments/group/${encodeURIComponent(newRunId)}`);
    } catch (e) {
      if (e instanceof BlockedClientsChangedError) {
        // The tags were read again for the start, and this page now shows the new list. The
        // message names what the start found, in case the client list changed as well.
        const now = describeSkips(e.skipped);
        setRunFailedError(now ? `${e.message} ${now}` : `${e.message} It now skips no client.`);
        return;
      }
      console.error('Failed to run the failed clients again', e);
      setConfirmRunFailed(false);
      setError(e instanceof Error ? e.message : 'Could not start the run. Try again.');
    } finally {
      setRunningFailed(false);
    }
  };

  if (!groupRunId) {
    return (
      <Alert variant="warning">This address has no group run in it. Open a group run from the Deployments page.</Alert>
    );
  }

  const image = summary ? imageDisplay(summary, imageNames) : null;
  const clientCount = counts?.total ?? 0;
  const waitingLabel = groupIsLive ? 'Waiting' : 'Not started';
  const filters: { key: MemberFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'running', label: 'Running' },
    { key: 'failed', label: 'Failed' },
    { key: 'succeeded', label: 'Succeeded' },
    { key: 'waiting', label: waitingLabel },
  ];

  return (
    <div className="deploy-page">
      <PageHeader
        title="Group run"
        // Which run this is: the group, the image, how many clients.
        description={
          summary && image
            ? `${groupDisplayName(summary)} · ${image.label} · ${clientCount} ${clientCount === 1 ? 'client' : 'clients'}`
            : undefined
        }
        back={{ to: '/deployments', label: 'Deployments' }}
        actions={
          <>
            {summary && groupIsLive && (
              <Button variant="outline-danger" onClick={() => openAction({ type: 'stop', record: summary })}>
                Stop group run
              </Button>
            )}
            {failedClients.length > 0 && !checkingFailed && (
              <Button
                variant="primary"
                onClick={() => {
                  setRunFailedError(null);
                  setConfirmRunFailed(true);
                }}
                disabled={!user?.email}
              >
                Run {failedClients.length} failed {failedClients.length === 1 ? 'client' : 'clients'} again
              </Button>
            )}
          </>
        }
      />

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {loading ? (
        <Card className="deploy-summary">
          <div className="deploy-empty">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading the group run
          </div>
        </Card>
      ) : !summary || !counts ? (
        <Alert variant="warning">
          No group run with this ID was found. It may be too old to list. Go back to the Deployments page and open it
          from there.
        </Alert>
      ) : (
        <Card className={`deploy-summary${groupIsLive ? ' deploy-live-card--live' : ''}`}>
          <div className="deploy-summary__head">
            <StatusPill status={summary.status} />
            <div className="deploy-counts numeric">
              <strong>
                {counts.done} of {counts.total} clients done
              </strong>
              <span className="deploy-count deploy-count--succeeded">{counts.succeeded} succeeded</span>
              <span className="deploy-count deploy-count--failed">{counts.failed} failed</span>
              {groupIsLive && <span className="deploy-count deploy-count--running">{counts.running} running</span>}
              {counts.waiting > 0 && (
                <span className="deploy-count deploy-count--waiting">
                  {counts.waiting} {waitingLabel.toLowerCase()}
                </span>
              )}
            </div>
            <div className="deploy-summary__tools">
              <RefreshButton
                onRefresh={() => {
                  retryUnavailableFailures();
                  void loadData();
                }}
                busy={refreshing}
                refreshedAt={refreshedAt}
                auto={anyLive}
              />
              <RunMoreMenu record={summary} />
            </div>
          </div>
          <SegmentedBar
            total={counts.total}
            segments={[
              { key: 'succeeded', label: 'Succeeded', count: counts.succeeded },
              { key: 'failed', label: 'Failed', count: counts.failed },
              { key: 'running', label: 'Running', count: counts.running },
              { key: 'waiting', label: waitingLabel, count: counts.waiting },
            ]}
          />
          <dl className="deploy-facts deploy-summary__facts">
            <div className="deploy-fact">
              <dt>Image</dt>
              <dd>
                <span className={image?.named ? undefined : 'mono'} title={image?.tag}>
                  {image?.label || 'Unknown'}
                </span>
                {image?.dev && (
                  <Badge bg="warning" className="ms-2">
                    Dev
                  </Badge>
                )}
              </dd>
            </div>
            <div className="deploy-fact">
              <dt>Started by</dt>
              <dd title={summary.initiatedBy}>{shortUser(summary.initiatedBy) || 'Unknown'}</dd>
            </div>
            <div className="deploy-fact">
              <dt>Started</dt>
              <dd title={formatNzDateTime(summary.startedAt)}>
                {timeAgo(summary.startedAt)}, {formatNzDateTime(summary.startedAt)}
              </dd>
            </div>
            <div className="deploy-fact">
              <dt>{groupIsLive ? 'Running for' : 'Took'}</dt>
              <dd>
                <GroupDuration summary={summary} />
              </dd>
            </div>
            <div className="deploy-fact">
              <dt>At once</dt>
              <dd>{summary.maxConcurrency ? `Up to ${summary.maxConcurrency} clients` : 'The portal default'}</dd>
            </div>
            <div className="deploy-fact">
              <dt>Group run ID</dt>
              <dd>
                <span className="mono" title={groupRunId}>
                  {groupRunId.length > 22 ? `${groupRunId.slice(0, 20)}…` : groupRunId}
                </span>
                <CopyButton value={groupRunId} label="Copy group run ID" />
              </dd>
            </div>
          </dl>
        </Card>
      )}

      {!loading && summary && skipped.length > 0 && (
        <section className="deploy-skipped">
          <h2 className="section-label">Skipped</h2>
          <p className="deploy-skipped__note">
            {skipped.length === 1 ? 'This client was' : `These ${skipped.length} clients were`} left out of the run when
            it started. The reason is beside each one.
          </p>
          <Card>
            <Table className="mb-0 deploy-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Reason</th>
                  <th>Tag set by</th>
                </tr>
              </thead>
              <tbody>
                {skipped.map((s) => (
                  <tr key={s.clientName}>
                    <td>
                      <span className="deploy-client">{s.clientName}</span>
                    </td>
                    <td className="deploy-skipped__reason">{skipLine(s)}</td>
                    <td title={s.setBy || undefined}>
                      {s.setBy ? shortPerson(s.setBy) : ''}
                      {s.setAt && <div className="deploy-sub">{formatNzDate(s.setAt)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </section>
      )}

      {!loading && summary && (
        <section className="deploy-section">
          <div className="deploy-toolbar">
            <Nav
              variant="pills"
              className="nav-pills-custom"
              activeKey={filter}
              onSelect={(key) => key && setFilter(key as MemberFilter)}
            >
              {filters
                .filter((f) => f.key === 'all' || filterCounts[f.key] > 0 || f.key === filter)
                .map((f) => (
                  <Nav.Item key={f.key}>
                    <Nav.Link eventKey={f.key}>
                      {f.label}
                      <span className="deploy-toolbar__count">{filterCounts[f.key]}</span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
            </Nav>
            <div className="deploy-toolbar__filters">
              <InputGroup size="sm" className="deploy-toolbar__search">
                <InputGroup.Text>
                  <Search size={12} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Find a client"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Find a client"
                />
              </InputGroup>
            </div>
          </div>

          <Card>
            <Table hover responsive className="mb-0 deploy-table deploy-table--members">
              {/* Set widths for every column but Status, which takes what is left. */}
              <colgroup>
                <col className="deploy-col--client" />
                <col />
                <col className="deploy-col--started" />
                <col className="deploy-col--duration" />
                <col className="deploy-col--actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS} className="deploy-empty">
                      {rows.length === 0
                        ? 'No client of this group has started yet.'
                        : 'No client matches that filter.'}
                    </td>
                  </tr>
                )}
                {visibleRows.map((member) => {
                  if (isPlaceholder(member)) {
                    return (
                      <tr key={member.deploymentId}>
                        <td>
                          <div className="deploy-client deploy-cut" title={member.clientName}>
                            {member.clientName}
                          </div>
                        </td>
                        <td>
                          <StatusPill status={member.status} />
                        </td>
                        <td />
                        <td />
                        <td />
                      </tr>
                    );
                  }
                  const open = Boolean(expanded[member.deploymentId]);
                  const failed = isFailed(member);
                  const live = isLive(member);
                  const newerRun = newerRunFor(member);
                  const blocked = liveClients.has(member.clientName)
                    ? 'A deploy for this client is running now.'
                    : undefined;
                  const failure = failures[member.deploymentId];
                  const block = flags.deployBlock(member.clientName);
                  // A running member carries its progress on a line of its own, there from the moment it is live.
                  const classes = [failed || live ? 'deploy-row--with-reason' : '', open ? 'deploy-row--open' : '']
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <Fragment key={member.deploymentId}>
                      <tr className={classes || undefined}>
                        <td>
                          <div className="deploy-client deploy-cut" title={member.clientName}>
                            {member.clientName}
                          </div>
                          {block && (
                            <div className="deploy-sub">
                              <DoNotDeployTag block={block} />
                            </div>
                          )}
                        </td>
                        <td>
                          <StatusPill status={member.status} attemptNumber={member.attemptNumber} />
                          {statusKey(member) === 'resolved' && member.resolvedBy && (
                            <div
                              className="deploy-sub deploy-cut"
                              title={[member.resolvedBy, formatNzDateTime(member.resolvedAt), member.resolvedNote]
                                .filter(Boolean)
                                .join('\n')}
                            >
                              Resolved by {shortUser(member.resolvedBy)}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="deploy-cut" title={formatNzDateTime(member.startedAt)}>
                            {timeAgo(member.startedAt)}
                          </div>
                          <div className="deploy-sub deploy-cut" title={formatNzDateTime(member.startedAt)}>
                            {formatNzDateTime(member.startedAt)}
                          </div>
                        </td>
                        <td>
                          <MemberDuration record={member} />
                        </td>
                        <td className="deploy-actions">
                          <Link
                            className="btn btn-sm btn-outline-secondary"
                            to={`/deployments/${encodeURIComponent(member.deploymentId)}/logs`}
                          >
                            Logs
                          </Link>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="deploy-actions__toggle"
                            onClick={() => setExpanded((prev) => ({ ...prev, [member.deploymentId]: !open }))}
                            aria-expanded={open}
                            aria-label={open ? 'Hide details' : 'Show details'}
                            title={open ? 'Hide details' : 'Show details'}
                          >
                            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </Button>
                          <RunMoreMenu
                            record={member}
                            {...handlers}
                            runAgainBlocked={blocked}
                            deployBlocked={Boolean(block)}
                          />
                        </td>
                      </tr>
                      {live && (
                        <tr className="deploy-row-reason">
                          <td colSpan={COLUMNS}>
                            <RowProgress record={member} snapshot={progress[member.deploymentId]} />
                          </td>
                        </tr>
                      )}
                      {failed && (
                        <tr className="deploy-row-reason">
                          <td colSpan={COLUMNS}>
                            <FailureLine
                              info={failure}
                              newerRun={newerRun}
                              // Until the check for a later run elsewhere is back, do not offer a second run.
                              hideOffer={!(member.clientName in latestByClient)}
                              onRunAgain={() => handlers.onRunAgain(member)}
                              runAgainBlocked={blocked}
                            />
                          </td>
                        </tr>
                      )}
                      {open && (
                        <tr className="deploy-row-details">
                          <td colSpan={COLUMNS}>
                            <RunDetails record={member} failure={failure} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        </section>
      )}

      <RunActionDialog
        action={action}
        newerRun={actionNewest}
        clientBusy={action?.type === 'run-again' ? liveClients.has(action.record.clientName) : false}
        failure={action ? failures[action.record.deploymentId] : undefined}
        onClose={() => setAction(null)}
        onDone={() => {
          setAction(null);
          void loadData();
        }}
      />

      <DeployBlockDialog clientName={blockingClient} onClose={() => setBlockingClient(null)} />

      <ConfirmDialog
        show={confirmRunFailed}
        title="Run the failed clients again?"
        confirmLabel={`Deploy to ${failedSplit.allowed.length} ${failedSplit.allowed.length === 1 ? 'client' : 'clients'}`}
        busy={runningFailed}
        error={runFailedError}
        blocked={
          knownClientsFailed
            ? 'The client list did not load, so a group run cannot start. Reload the page.'
            : !knownClients
              ? 'Reading the client list.'
              : failedClients.length > 0 && failedSplit.allowed.length === 0
                ? 'Every client that failed here is skipped, so there is nothing to start.'
                : undefined
        }
        onCancel={() => setConfirmRunFailed(false)}
        onConfirm={() => void runFailedAgain()}
      >
        <p>
          This starts a new group run with the same image for the {failedSplit.allowed.length}{' '}
          {failedSplit.allowed.length === 1 ? 'client' : 'clients'} that failed here and{' '}
          {failedSplit.allowed.length === 1 ? 'was' : 'were'} not deployed again since.
          {movedOn > 0 &&
            ` ${movedOn} more failed here and ${movedOn === 1 ? 'was' : 'were'} deployed again later, so ${movedOn === 1 ? 'it is' : 'they are'} left out.`}
        </p>
        <div className="deploy-chips">
          {failedSplit.allowed.map((client) => (
            <Badge bg="secondary" key={client}>
              {client}
            </Badge>
          ))}
        </div>
        <SkippedClientsNotice skipped={failedSplit.skipped} className="mt-3 mb-0" />
      </ConfirmDialog>
    </div>
  );
}
